// src/pages/ServiceByLocation.jsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { catalogApi } from "../api/client";
import ListingCard from "../Components/ListingCard";
import FilterSideBar from "../Components/FilterSideBar";
import SearchBar from "../Components/SearchBar";
import Navbar from "../Components/Navbar";
import HalfHero from "../Images/main.jpg";

// Same static states as in Location.jsx
const STATES = [
  { name: "Andhra Pradesh", lat: 15.9129, lng: 79.74 },
  { name: "Arunachal Pradesh", lat: 28.218, lng: 94.7278 },
  { name: "Assam", lat: 26.2006, lng: 92.9376 },
  { name: "Bihar", lat: 25.0961, lng: 85.3131 },
  { name: "Chhattisgarh", lat: 21.2787, lng: 81.8661 },
  { name: "Goa", lat: 15.2993, lng: 74.124 },
  { name: "Gujarat", lat: 22.2587, lng: 71.1924 },
  { name: "Haryana", lat: 29.0588, lng: 76.0856 },
  { name: "Himachal Pradesh", lat: 31.1048, lng: 77.1734 },
  { name: "Jharkhand", lat: 23.6102, lng: 85.2799 },
  { name: "Karnataka", lat: 15.3173, lng: 75.7139 },
  { name: "Kerala", lat: 10.8505, lng: 76.2711 },
  { name: "Madhya Pradesh", lat: 22.9734, lng: 78.6569 },
  { name: "Maharashtra", lat: 19.7515, lng: 75.7139 },
  { name: "Manipur", lat: 24.6637, lng: 93.9063 },
  { name: "Meghalaya", lat: 25.467, lng: 91.3662 },
  { name: "Mizoram", lat: 23.1645, lng: 92.9376 },
  { name: "Nagaland", lat: 26.1584, lng: 94.5624 },
  { name: "Odisha", lat: 20.9517, lng: 85.0985 },
  { name: "Punjab", lat: 31.1471, lng: 75.3412 },
  { name: "Rajasthan", lat: 27.0238, lng: 74.2179 },
  { name: "Sikkim", lat: 27.533, lng: 88.5122 },
  { name: "Tamil Nadu", lat: 11.1271, lng: 78.6569 },
  { name: "Telangana", lat: 18.1124, lng: 79.0193 },
  { name: "Tripura", lat: 23.9408, lng: 91.9882 },
  { name: "Uttar Pradesh", lat: 26.8467, lng: 80.9462 },
  { name: "Uttarakhand", lat: 30.0668, lng: 79.0193 },
  { name: "West Bengal", lat: 22.9868, lng: 87.855 },
];

// Haversine formula for distance in km
function getDistanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const ServiceByLocation = () => {
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Get ?location=STATE from URL
  const params = new URLSearchParams(location.search);
  const stateName = params.get("location");
  const companyType = params.get("companyType") || "";

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (companyType) query.companyType = companyType;
    catalogApi
      .publicSearch(query)
      .then((data) => {
        const allServices = Array.isArray(data?.items) ? data.items : [];
        // Find selected state
        const state = STATES.find(
          (s) => s.name.toLowerCase() === (stateName || "").toLowerCase()
        );
        if (!state) {
          setServices(allServices);
          setLoading(false);
          return;
        }
        // For each service, calculate distance from state center if coordinates exist
        const withDistance = allServices.map((service) => {
          const coords = service?.location?.coordinates;
          if (
            Array.isArray(coords) &&
            coords.length === 2 &&
            typeof coords[0] === "number" &&
            typeof coords[1] === "number"
          ) {
            const [lng, lat] = coords;
            return {
              ...service,
              distanceKm: getDistanceKm(state.lat, state.lng, lat, lng),
            };
          }
          return { ...service, distanceKm: null };
        });
        // Sort by distance (nulls last)
        withDistance.sort((a, b) => {
          if (a.distanceKm == null) return 1;
          if (b.distanceKm == null) return -1;
          return a.distanceKm - b.distanceKm;
        });
        setServices(withDistance);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [stateName]);

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-orange-400 via-white to-green-400">
        <div className="relative flex items-center justify-center h-24 w-24 mb-6">
          <span className="absolute inline-flex h-full w-full rounded-full bg-gradient-to-tr from-orange-500 to-green-500 opacity-30 animate-ping"></span>
          <svg
            className="animate-spin h-16 w-16 text-orange-600 drop-shadow-lg"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-20"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-80"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        </div>
        <div className="text-2xl font-semibold text-gray-800 tracking-wide">
          Loading services...
        </div>
        <div className="text-sm text-gray-600 mt-2">
          Please wait while we fetch the best options for you.
        </div>
      </div>
    );

  return (
    <>
      <section className="relative w-full h-1/2 overflow-hidden">
        {/* Background Image (with blur) */}
        <img
          src={HalfHero}
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover z-[-10] blur-sm"
        />

        {/* Navbar */}
        <Navbar />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/80 z-[-5]" />

        {/* Content on top of image */}
        <div className="relative z-10 h-full text-white flex flex-col justify-center items-center">
          <div className="h-3/4 w-3/4 text-white flex flex-col justify-center items-center gap-5">
            <h1 className="text-xl font-bold text-center mulish-light uppercase">
              Welcome to <br />
              <span className="text-5xl mulish-bold tracking-wider bg-gradient-to-r from-orange-500 via-white to-green-500 bg-clip-text text-transparent ">
                ONLINE INDIAN MARKET
              </span>
            </h1>
            <p className="mt-2 text-sm mulish-light tracking-wider text-center uppercase">
              Get the Nearest and Best Services in the Market.
            </p>
            <SearchBar />
            <div className="flex flex-row justify-around w-3/4"></div>
          </div>
        </div>
      </section>
      <section className="flex flex-col justify-center px-10 items-center">
        <div className="w-full flex flex-col md:flex-row justify-center items-center md:items-start md:gap-6 ">
          {/* Mobile toggle button */}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="md:hidden px-4 py-2 bg-blue-500 text-white rounded-lg shadow mt-4"
          >
            Filters
          </button>

          {/* Sidebar: no subcategories for location, so pass empty array */}
          <div className="w-1/4 sticky top-0 p-0">
            {/* Sidebar */}
            <FilterSideBar
              isOpen={isSidebarOpen}
              onClose={() => setIsSidebarOpen(false)}
            />
          </div>

          <div className="md:w-3/4 my-10 overflow-y-auto pr-2 w-full space-y-6">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Services in {stateName}
            </h2>
            {services.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No services found.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 w-full">
                {services.map((service) => (
                  <ListingCard key={service._id} listing={service} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceByLocation;
